import { accessTokenInterceptor } from '@teko/auth';
import { getCartToken } from '@teko/cart/dist/v2';
import {
  GetProductBundleOrComboParams,
  GetProductBundleOrComboResponse,
  ICreateProductReviewPayload,
  ICreateProductReviewResponse,
  ICreateStudentInfoPayload,
  ICreateStudentInfoResponse,
  IGetCustomerProfileRequest,
  IGetCustomerProfileResponse,
  IGetProductReviewResponse,
  IGetProductReviewsParams,
  ISendOTPRequest,
  ISendOrVerifyOTPResponse,
  ISubscribeProductStockPayload,
  IVerifyOTPRequest,
  PunchoutCheckoutPayload,
  PunchoutCheckoutResponse,
} from 'types';
import { consumerBffApi } from './clients';

// API doc: https://apidoc.teko.vn/project-doc/draft/bff_consumers/retail_buyer/version/latest/operations/GetProductReviews
export const getProductReviews = async (
  productId: number,
  params: IGetProductReviewsParams,
): Promise<IGetProductReviewResponse> => {
  const { data } = await consumerBffApi.get(
    `api/v1/products/${productId}/reviews`,
    { params: { ...params } },
  );
  return data.data;
};

export const createProductReview = async (
  productId: number,
  payload: ICreateProductReviewPayload,
): Promise<ICreateProductReviewResponse> => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.post(
    `api/v1/products/${productId}/reviews`,
    payload,
  );
  return res.data;
};

export const getCustomerProfile = async (
  params: IGetCustomerProfileRequest,
): Promise<IGetCustomerProfileResponse> => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.get(`api/v1/profiles`, {
    params: { ...params },
  });
  return res.data;
};

export const createStudentInfo = async (
  profileId: string,
  payload: ICreateStudentInfoPayload,
  language?: string,
): Promise<ICreateStudentInfoResponse> => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.post(
    `api/v1/profiles/${profileId}/student-info`,
    payload,
    {
      headers: {
        'Accept-Language': language || '*',
      },
    },
  );
  return res.data;
};

export const sendOTPToEmail = async (
  payload: ISendOTPRequest,
): Promise<ISendOrVerifyOTPResponse> => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.post(
    `api/v1/profiles/otp/send-email`,
    payload,
  );
  return res.data;
};

export const verifyOTP = async (
  payload: IVerifyOTPRequest,
): Promise<ISendOrVerifyOTPResponse> => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.post(
    `api/v1/profiles/otp/verify-email`,
    payload,
  );
  return res.data;
};

export const subscribeToProductStockNotifications = async (
  payload: ISubscribeProductStockPayload,
) => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.post(`api/v1/product/subscribe`, payload);
  return res.data;
};

export const getProductBundleOrCombo = async (
  params: GetProductBundleOrComboParams,
  language: string = 'vi',
): Promise<GetProductBundleOrComboResponse> => {
  consumerBffApi.addInterceptor(accessTokenInterceptor);
  const res = await consumerBffApi.get('api/v1/combo-bundles', {
    params,
    headers: { 'Accept-Language': language || '*' },
  });
  return res.data;
};

export const createPunchoutOrder = async (
  payload: PunchoutCheckoutPayload,
): Promise<PunchoutCheckoutResponse> => {
  consumerBffApi.addInterceptor((request) => {
    accessTokenInterceptor(request);
    const cartToken = getCartToken();
    if (cartToken) (request.headers as Headers).set('x-cart-token', cartToken);
  });

  const res = await consumerBffApi.post('api/v1/punchout/checkout', payload);
  return res.data;
};
